import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { AlertTriangle, ArrowLeft, FileText, ShieldAlert } from "lucide-react";
import TopBar from "../../components/TopBar";
import { ACTIVITY_TYPES, INSPECTOR_PANIC_STEPS, OPERATOR_PANIC_STEPS, PANIC_SESSION_MODES } from "./panicConfig";
import { detectRiskyPhrase } from "./riskRules";
import { usePanicSession } from "./usePanicSession";
import PanicStepCard from "./PanicStepCard";
import PanicQuickReplies from "./PanicQuickReplies";
import PanicCorrectiveActionModal from "./PanicCorrectiveActionModal";
import InspectorOverview from "./InspectorOverview";

export default function PanicModePage() {
  const location = useLocation();
  const [sessionMode, setSessionMode] = useState(null);
  const activeSteps = sessionMode === "inspector" ? INSPECTOR_PANIC_STEPS : OPERATOR_PANIC_STEPS;
  const panic = usePanicSession(activeSteps);
  const {
    session, quickReplies, loading, saving, error, stepsByKey, progress,
    hydrate, reset, start, saveStep, saveCorrectiveAction, saveNote, complete,
  } = panic;

  const [activityType, setActivityType] = useState(ACTIVITY_TYPES[0]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [riskyText, setRiskyText] = useState("");
  const [riskHit, setRiskHit] = useState(null);
  const [modalStep, setModalStep] = useState(null);
  const [pendingNote, setPendingNote] = useState("");
  const [notes, setNotes] = useState("");
  const [inspectorNotes, setInspectorNotes] = useState("");
  const [responsibleName, setResponsibleName] = useState("");
  const [inviteModalOpen, setInviteModalOpen] = useState(false);
  const [inviteLoading, setInviteLoading] = useState(false);
  const [inviteError, setInviteError] = useState("");
  const [inviteData, setInviteData] = useState(null);
  const [inspectors, setInspectors] = useState([]);
  const [inspectorsLoading, setInspectorsLoading] = useState(false);
  const [canInspectorMode, setCanInspectorMode] = useState(false);

  const activeStep = activeSteps[activeIndex] || activeSteps[0];
  const activeRow = stepsByKey.get(activeStep.id);
  const completedSession = session?.status === "completed";
  const exportUrl = session?.id ? `/api/panic/export-log.php?session_id=${encodeURIComponent(session.id)}` : null;
  const activityLabel = session?.activity_type_label || activityType;
  const isInspectorMode = sessionMode === "inspector";
  const hasModeSelection = Boolean(sessionMode);
  const availableModes = useMemo(
    () => PANIC_SESSION_MODES.filter((mode) => mode.id !== "inspector" || canInspectorMode),
    [canInspectorMode]
  );

  const summaryRows = useMemo(() => {
    return activeSteps.map((step) => ({ step, row: stepsByKey.get(step.id) }));
  }, [activeSteps, stepsByKey]);

  useEffect(() => {
    if (!session) return;
    setNotes(session.notes || "");
    setInspectorNotes(session.inspector_notes || "");
    setResponsibleName(session.responsible_name || "");
  }, [session]);

  useEffect(() => {
    setSessionMode(null);
    setActiveIndex(0);
    setNotes("");
    setInspectorNotes("");
    setResponsibleName("");
    setPendingNote("");
    setModalStep(null);
    setRiskyText("");
    setRiskHit(null);
    reset();
  }, [location.key, reset]);

  useEffect(() => {
    let alive = true;
    fetch("/api/me.php", { credentials: "include" })
      .then((res) => res.json().catch(() => null))
      .then((me) => {
        if (!alive) return;
        const userPerms = Array.isArray(me?.permessi) ? me.permessi : [];
        setCanInspectorMode(
          me?.ruolo === "superadmin" ||
            userPerms.includes("ALL") ||
            userPerms.includes("PANIC_MODE")
        );
      })
      .catch(() => {
        if (alive) setCanInspectorMode(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  useEffect(() => {
    if (sessionMode || !canInspectorMode) return;

    let alive = true;
    setInspectorsLoading(true);

    fetch("/api/inspector/list.php", { credentials: "include" })
      .then((res) => res.json().catch(() => null))
      .then((data) => {
        if (!alive) return;
        setInspectors(Array.isArray(data?.items) ? data.items : []);
      })
      .catch(() => {
        if (!alive) return;
        setInspectors([]);
      })
      .finally(() => {
        if (alive) setInspectorsLoading(false);
      });

    return () => {
      alive = false;
    };
  }, [sessionMode, canInspectorMode]);

  async function handleSelectMode(mode) {
    if (mode === "inspector" && !canInspectorMode) return;
    setSessionMode(mode);
    setActiveIndex(0);
    if (mode === "operator") {
      await hydrate(mode);
      return;
    }
    reset();
  }

  function handleResetMode() {
    reset();
    setSessionMode(null);
    setActiveIndex(0);
    setNotes("");
    setInspectorNotes("");
    setResponsibleName("");
    setPendingNote("");
    setModalStep(null);
  }

  async function handleStart(reuse = false) {
    const started = await start(activityType, sessionMode || "operator", reuse);
    if (started?.notes) setNotes(started.notes);
    if (started?.inspector_notes) setInspectorNotes(started.inspector_notes);
  }

  async function handleSaveStep(status, valueText = "") {
    await saveStep(activeStep, status, valueText);
    if (activeIndex < activeSteps.length - 1) setActiveIndex((value) => value + 1);
  }

  function handleNonOk(valueText) {
    setPendingNote(valueText);
    setModalStep(activeStep);
  }

  async function handleSaveCorrectiveAction(action) {
    await saveStep(modalStep, "NON_OK", pendingNote);
    await saveCorrectiveAction(modalStep, action);
    setModalStep(null);
    setPendingNote("");
    if (activeIndex < activeSteps.length - 1) setActiveIndex((value) => value + 1);
  }

  function checkRisk(text) {
    setRiskyText(text);
    setRiskHit(detectRiskyPhrase(text));
  }

  async function handleGenerateInspectorQr() {
    if (!canInspectorMode) return;
    setInviteModalOpen(true);
    setInviteLoading(true);
    setInviteError("");

    try {
      const res = await fetch("/api/inspector/create-invite.php", {
        method: "POST",
        credentials: "include",
      });
      const data = await res.json().catch(() => null);
      if (!res.ok || !data?.ok) {
        throw new Error(data?.error || data?.message || `HTTP ${res.status}`);
      }
      setInviteData(data);
    } catch (err) {
      setInviteError(err.message || "Impossibile generare il QR code ispettore");
    } finally {
      setInviteLoading(false);
    }
  }

  if (loading && sessionMode) {
    return (
      <>
        <TopBar title="Panic Mode" />
        <div className="p-6 text-slate-600">Caricamento Panic Mode...</div>
      </>
    );
  }

  return (
    <>
      <TopBar title="Panic Mode" />
      <div className="min-h-screen bg-slate-100 p-4 md:p-6">
        <div className="mx-auto max-w-6xl space-y-4">
          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div className="flex items-center gap-3">
                <div className="grid h-11 w-11 place-items-center rounded-2xl bg-red-600 text-white">
                  <ShieldAlert size={22} />
                </div>
                <div>
                  <div className="text-xs font-semibold uppercase tracking-wide text-red-600">
                    {!hasModeSelection ? "Panic Mode" : isInspectorMode ? "Panic Mode / Ispettore" : "Panic Mode / Operatore"}
                  </div>
                  <div className="text-xl font-bold text-slate-950">
                    {!hasModeSelection
                      ? "Scegli come vuoi entrare: Operatore oppure Ispettore."
                      : isInspectorMode
                        ? "Preparazione guidata a NAS e ASL, una prova alla volta."
                        : "Norma aiuta l'operatore durante il controllo, una prova alla volta."}
                  </div>
                </div>
              </div>
              <a href="/" className="inline-flex items-center gap-2 rounded-xl border px-3 py-2 text-sm font-semibold hover:bg-slate-50">
                <ArrowLeft size={16} />
                Dashboard
              </a>
            </div>
          </div>

          <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Modalita Panic Mode</div>
                <div className="text-sm text-slate-600">
                  {!hasModeSelection
                    ? "I due ingressi sono qui sotto: scegli Operatore o Ispettore per iniziare."
                    : "Scegli sempre da qui se vuoi supporto operatore o checklist ispettiva."}
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                {availableModes.map((mode) => {
                  const active = sessionMode === mode.id;
                  return (
                    <button
                      key={mode.id}
                      type="button"
                      onClick={() => {
                        if (mode.id === sessionMode) return;
                        handleResetMode();
                        setTimeout(() => handleSelectMode(mode.id), 0);
                      }}
                      className={[
                        "rounded-xl border px-4 py-2 text-sm font-bold",
                        active ? "bg-slate-950 text-white border-slate-950" : "bg-white hover:bg-slate-50",
                      ].join(" ")}
                    >
                      {mode.title}
                    </button>
                  );
                })}
                <button
                  type="button"
                  onClick={handleResetMode}
                  className="rounded-xl border px-4 py-2 text-sm font-semibold hover:bg-slate-50"
                >
                  Home Panic Mode
                </button>
              </div>
            </div>
          </div>

          {error && (
            <div className="rounded-2xl border border-red-200 bg-red-50 p-4 text-sm font-semibold text-red-800">
              {error}
            </div>
          )}

          {!sessionMode ? (
            <ModeChoiceCard
              onSelect={handleSelectMode}
              onGenerateInspectorQr={handleGenerateInspectorQr}
              inspectors={inspectors}
              inspectorsLoading={inspectorsLoading}
              canInspectorMode={canInspectorMode}
            />
          ) : isInspectorMode ? (
            <InspectorOverview steps={INSPECTOR_PANIC_STEPS} />
          ) : !session ? (
            <StartCard
              sessionMode={sessionMode}
              activityType={activityType}
              setActivityType={setActivityType}
              saving={saving}
              onStart={() => handleStart(false)}
              onReuse={() => handleStart(true)}
              onBack={handleResetMode}
            />
          ) : (
            <div className="grid gap-4 lg:grid-cols-[1fr_360px]">
              <main className="space-y-4">
                <StepProgress
                  activeIndex={activeIndex}
                  setActiveIndex={setActiveIndex}
                  stepsByKey={stepsByKey}
                  progress={progress}
                  steps={activeSteps}
                />

                {!completedSession ? (
                  <PanicStepCard
                    key={activeStep.id}
                    step={activeStep}
                    row={activeRow}
                    saving={saving}
                    isLast={activeIndex === activeSteps.length - 1}
                    onSave={handleSaveStep}
                    onNonOk={handleNonOk}
                    onNext={() => setActiveIndex((value) => Math.min(value + 1, activeSteps.length - 1))}
                  />
                ) : (
                  <CompletedCard exportUrl={exportUrl} />
                )}

                <RiskGuard riskyText={riskyText} riskHit={riskHit} onChange={checkRisk} />
              </main>

              <aside className="space-y-4">
                <PanicQuickReplies replies={quickReplies} activeIds={activeStep.quickReplyIds} />
                <FinalLogCard
                  activityLabel={activityLabel}
                  sessionMode={sessionMode}
                  notes={notes}
                  inspectorNotes={inspectorNotes}
                  responsibleName={responsibleName}
                  setNotes={setNotes}
                  setInspectorNotes={setInspectorNotes}
                  setResponsibleName={setResponsibleName}
                  saving={saving}
                  completedSession={completedSession}
                  exportUrl={exportUrl}
                  onSaveNotes={() => saveNote(notes, inspectorNotes)}
                  onComplete={() => complete({ notes, inspectorNotes, responsibleName })}
                />
                <SummaryCard summaryRows={summaryRows} />
              </aside>
            </div>
          )}
        </div>
      </div>

      {modalStep && (
        <PanicCorrectiveActionModal
          step={modalStep}
          saving={saving}
          onClose={() => setModalStep(null)}
          onSave={handleSaveCorrectiveAction}
        />
      )}

      {inviteModalOpen && (
        <InspectorInviteModal
          loading={inviteLoading}
          error={inviteError}
          inviteData={inviteData}
          onClose={() => setInviteModalOpen(false)}
        />
      )}
    </>
  );
}

function ModeChoiceCard({ onSelect, onGenerateInspectorQr, inspectors = [], inspectorsLoading = false, canInspectorMode = false }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="max-w-3xl">
        <h1 className="text-2xl font-bold text-slate-950">Come vuoi usare Panic Mode?</h1>
        <p className="mt-2 text-slate-600">Scegli se sei l'operatore che chiede supporto a Norma oppure se stai seguendo la checklist ispettiva.</p>
        <div className="mt-5 grid gap-3 md:grid-cols-2">
          {PANIC_SESSION_MODES.filter((mode) => mode.id !== "inspector" || canInspectorMode).map((mode) => (
            <button
              key={mode.id}
              type="button"
              onClick={() => onSelect(mode.id)}
              className="rounded-2xl border border-slate-200 bg-slate-50 p-5 text-left transition hover:border-slate-300 hover:bg-white"
            >
              <div className="text-lg font-bold text-slate-950">{mode.title}</div>
              <div className="mt-2 text-sm text-slate-600">{mode.subtitle}</div>
            </button>
          ))}
        </div>
        {canInspectorMode && <div className="mt-5 rounded-2xl border border-sky-200 bg-sky-50 p-4">
          <div className="text-sm font-semibold text-sky-950">Accesso esterno ispettore</div>
          <p className="mt-1 text-sm text-sky-900">
            Genera un QR code dedicato: l'ispettore lo scansiona, si registra dal telefono e vede solo l'area ispettore della tua azienda.
          </p>
          <div className="mt-2 text-xs text-sky-900">
            Ispettore gia registrato: entra direttamente da <strong>/login</strong> con email e password.
          </div>
          <button
            type="button"
            onClick={onGenerateInspectorQr}
            className="mt-4 rounded-xl bg-slate-950 px-4 py-2 text-sm font-bold text-white hover:bg-slate-800"
          >
            Genera QR code per ispettore
          </button>

          <div className="mt-4 rounded-2xl border border-sky-200 bg-white p-4">
            <div className="text-sm font-semibold text-slate-950">Ispettori registrati attivi</div>
            {inspectorsLoading ? (
              <div className="mt-2 text-sm text-slate-600">Caricamento elenco ispettori...</div>
            ) : inspectors.length === 0 ? (
              <div className="mt-2 text-sm text-slate-600">Nessun ispettore registrato al momento.</div>
            ) : (
              <div className="mt-3 space-y-2">
                {inspectors.filter((item) => item.active).map((item) => (
                  <div key={item.id} className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
                    <div className="font-semibold text-slate-900">{item.nome || item.email}</div>
                    <div className="text-slate-600">{item.email}</div>
                    <div className="text-xs text-slate-500">Registrato il {item.created_at}</div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>}
      </div>
    </div>
  );
}

function StartCard({ sessionMode, activityType, setActivityType, saving, onStart, onReuse, onBack }) {
  const isInspectorMode = sessionMode === "inspector";
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
      <div className="max-w-2xl">
        <h1 className="text-2xl font-bold text-slate-950">
          {isInspectorMode ? "Panic Mode ispettore" : "Panic Mode operatore"}
        </h1>
        <p className="mt-2 text-slate-600">
          {isInspectorMode
            ? "Usalo durante una visita improvvisa oppure come verifica interna periodica consigliata ogni 3 mesi."
            : "Versione classica in cui l'operatore si fa guidare da Norma durante il controllo."}
        </p>
        <div className="mt-4 rounded-2xl border border-sky-200 bg-sky-50 p-4 text-sm text-sky-950">
          {isInspectorMode
            ? "Il flusso ti accompagna tra documenti, HACCP, tracciabilita, allergeni, igiene, struttura, servizi esterni e non conformita."
            : "Norma ti aiuta a trovare subito documenti, temperature, pulizie, etichette e frasi corrette da usare."}
        </div>
        <label className="mt-5 block">
          <span className="text-xs font-semibold text-slate-500">Che tipo di attivita stai gestendo adesso?</span>
          <select
            className="mt-1 w-full max-w-md rounded-xl border px-3 py-2"
            value={activityType}
            onChange={(event) => setActivityType(event.target.value)}
          >
            {ACTIVITY_TYPES.map((item) => <option key={item} value={item}>{item}</option>)}
          </select>
        </label>
        <div className="mt-5 flex flex-wrap gap-2">
          <button type="button" onClick={onBack} className="rounded-xl border px-4 py-3 text-sm font-bold hover:bg-slate-50">
            Indietro
          </button>
          <button type="button" disabled={saving} onClick={onStart} className="rounded-xl bg-red-600 px-4 py-3 text-sm font-bold text-white hover:bg-red-700 disabled:opacity-60">
            {isInspectorMode ? "Avvia ispezione guidata" : "Avvia supporto operatore"}
          </button>
          <button type="button" disabled={saving} onClick={onReuse} className="rounded-xl border px-4 py-3 text-sm font-bold hover:bg-slate-50 disabled:opacity-60">
            Riprendi bozza
          </button>
        </div>
      </div>
    </div>
  );
}

function InspectorInviteModal({ loading, error, inviteData, onClose }) {
  const qrUrl = inviteData?.invite_url
    ? `https://api.qrserver.com/v1/create-qr-code/?size=280x280&data=${encodeURIComponent(inviteData.invite_url)}`
    : null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-lg rounded-2xl bg-white p-5 shadow-2xl">
        <div className="flex items-start justify-between gap-3">
          <div>
            <div className="text-lg font-bold text-slate-950">QR code ispettore</div>
            <div className="text-sm text-slate-600">
              L'ispettore si registra dal telefono e da quel momento vede solo l'area ispettore della tua azienda.
            </div>
          </div>
          <button type="button" onClick={onClose} className="rounded-xl border px-3 py-2 text-sm font-semibold hover:bg-slate-50">
            Chiudi
          </button>
        </div>

        {loading && <div className="mt-4 text-sm text-slate-600">Generazione QR code in corso...</div>}
        {error && <div className="mt-4 rounded-xl border border-red-200 bg-red-50 p-3 text-sm font-semibold text-red-800">{error}</div>}

        {inviteData?.invite_url && (
          <div className="mt-4 space-y-4">
            {qrUrl && (
              <div className="flex justify-center rounded-2xl border border-slate-200 bg-slate-50 p-4">
                <img src={qrUrl} alt="QR code ispettore" className="h-72 w-72 max-w-full" />
              </div>
            )}

            <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
              Link diretto: <a href={inviteData.invite_url} className="font-semibold text-sky-700 underline">{inviteData.invite_url}</a>
            </div>

            <div className="text-xs text-slate-500">
              Invito valido fino a: {inviteData.expires_at}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function StepProgress({ activeIndex, setActiveIndex, stepsByKey, progress, steps }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-3 shadow-sm">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="text-sm font-semibold text-slate-700">
          Step {activeIndex + 1} di {steps.length} - {progress.done}/{progress.total} tracciati
        </div>
        <div className="flex flex-wrap gap-2">
          {steps.map((step, index) => {
            const row = stepsByKey.get(step.id);
            const done = ["OK", "NON_OK", "NA"].includes(row?.status);
            return (
              <button
                key={step.id}
                type="button"
                onClick={() => setActiveIndex(index)}
                className={[
                  "h-8 min-w-8 rounded-full border px-2 text-xs font-bold",
                  index === activeIndex ? "bg-slate-950 text-white" : done ? "bg-emerald-50 text-emerald-700" : "bg-white text-slate-600",
                ].join(" ")}
                title={step.title}
              >
                {index + 1}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function RiskGuard({ riskyText, riskHit, onChange }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="flex items-start gap-3">
        <AlertTriangle className="mt-1 shrink-0 text-red-600" size={18} />
        <div className="w-full">
          <div className="font-semibold text-slate-950">Filtro frasi rischiose</div>
          <p className="text-sm text-slate-600">Scrivi cosa stai per dire. Norma blocca le frasi pericolose.</p>
          <textarea
            className="mt-3 min-h-[80px] w-full rounded-xl border px-3 py-2 text-sm"
            value={riskyText}
            onChange={(event) => onChange(event.target.value)}
            placeholder="Es. non lo facciamo sempre..."
          />
          {riskHit && (
            <div className="mt-3 rounded-xl border border-red-200 bg-red-50 p-3 text-sm font-bold text-red-800">
              {riskHit.response}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function CompletedCard({ exportUrl }) {
  return (
    <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-5">
      <h2 className="text-xl font-bold text-emerald-950">Hai tracciato tutto.</h2>
      <p className="mt-1 text-sm text-emerald-900">Ora sei tu al timone: ordinato, coerente, verificabile.</p>
      {exportUrl && (
        <a href={exportUrl} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-2 rounded-xl bg-emerald-700 px-4 py-2 text-sm font-bold text-white hover:bg-emerald-800">
          <FileText size={16} />
          Apri log finale
        </a>
      )}
    </div>
  );
}

function FinalLogCard(props) {
  const {
    activityLabel, sessionMode,
    notes, inspectorNotes, responsibleName, setNotes, setInspectorNotes, setResponsibleName,
    saving, completedSession, exportUrl, onSaveNotes, onComplete,
  } = props;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Log finale</div>
      <div className="mt-2 rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
        Modalita: <strong>{sessionMode === "inspector" ? "Ispettore" : "Operatore"}</strong>
        {" · "}
        Attivita: <strong>{activityLabel}</strong>
      </div>
      <div className="mt-2 space-y-3">
        <label className="block">
          <span className="text-xs font-semibold text-slate-500">Note ispezione</span>
          <textarea className="mt-1 min-h-[80px] w-full rounded-xl border px-3 py-2 text-sm" value={inspectorNotes} onChange={(event) => setInspectorNotes(event.target.value)} placeholder="Cosa ha chiesto l'ispettore?" />
        </label>
        <label className="block">
          <span className="text-xs font-semibold text-slate-500">Note interne</span>
          <textarea className="mt-1 min-h-[80px] w-full rounded-xl border px-3 py-2 text-sm" value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="Azioni o osservazioni utili." />
        </label>
        <label className="block">
          <span className="text-xs font-semibold text-slate-500">Responsabile</span>
          <input className="mt-1 w-full rounded-xl border px-3 py-2 text-sm" value={responsibleName} onChange={(event) => setResponsibleName(event.target.value)} placeholder="Nome responsabile presente" />
        </label>
        <div className="rounded-xl border border-slate-200 bg-slate-50 p-3 text-xs text-slate-600">
          Suggerimento operativo: usa questo log anche come audit simulato trimestrale e annota le azioni da chiudere prima della prossima verifica.
        </div>
        <div className="grid grid-cols-1 gap-2">
          <button type="button" disabled={saving} onClick={onSaveNotes} className="rounded-xl border px-4 py-2 text-sm font-bold hover:bg-slate-50 disabled:opacity-60">Salva note</button>
          {!completedSession && (
            <button type="button" disabled={saving} onClick={onComplete} className="rounded-xl bg-slate-950 px-4 py-2 text-sm font-bold text-white hover:bg-slate-800 disabled:opacity-60">Chiudi ispezione</button>
          )}
          {exportUrl && <a href={exportUrl} target="_blank" rel="noreferrer" className="rounded-xl border px-4 py-2 text-center text-sm font-bold hover:bg-slate-50">Export log HTML</a>}
        </div>
      </div>
    </div>
  );
}

function SummaryCard({ summaryRows }) {
  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <div className="text-xs font-semibold uppercase tracking-wide text-slate-500">Riepilogo audit</div>
      <div className="mt-3 space-y-2">
        {summaryRows.map(({ step, row }) => (
          <div key={step.id} className="flex items-start justify-between gap-3 rounded-xl border bg-slate-50 p-3">
            <div>
              <div className="text-sm font-semibold text-slate-900">{step.title}</div>
              {row?.value_text && <div className="mt-1 line-clamp-2 text-xs text-slate-600">{row.value_text}</div>}
            </div>
            <div className="text-xs font-bold text-slate-700">{row?.status || "PENDING"}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
